import {HistoryRecordBaseModel} from "./history-record-base.model";
import {GamingHistoryRecordRawData} from "../../gaming-history.service.interfce";
import {IHistoryRecordAmountViewModel, IHistoryRecordIdViewModel} from "./history-record-view-model.interface";
import {CyClass} from "../../../../../common/cy-classes";
import {Check} from "@sparkware/app-utils";
import {GameModeEnum} from "../../../game-runner/interfaces/game-context/game-context-view-model.interface";

export class UserGamingHistoryRecordModel extends HistoryRecordBaseModel<GamingHistoryRecordRawData> {
    get date(): string {
        if(this.recordData.DateInTimeZone) {
            return this.services.time.formatHistoryRecordDateTime(this.recordData.DateInTimeZone,  true);
        } else {
            return this.services.time.formatHistoryRecordDateTime(this.recordData.Date,  false);
        }
    }

    get ids(): IHistoryRecordIdViewModel[] {
        if(this._gameSessionID && this._participationTicketID) {
            return [
                {
                    text: this.translation.translate('GAME_SESSION_ID', {sessionID: this._gameSessionID}),
                    cyClass: CyClass('cy-gaming-history-session-id')
                },
                {
                    text: this.translation.translate('GAME_PARTICIPATION_ID', {participationID: this._participationTicketID}),
                    cyClass: CyClass('cy-gaming-history-participation-id')
                }
            ]
        }
        return [
            {
                text: this.recordData.GameId,
                cyClass: CyClass('cy-gaming-history-round-id')
            }
        ];
    }

    protected _isDetailHistorySupported(): boolean {
        return this.recordData.IsSupportDetailedHistory
            && this._isDetailHistoryAllowedOnCurrentPlatform()
            && this.company.supportsHistoryMoreDetails(this.recordData);
    }

    get bigWinMessage(): string | null {
        if(!this.recordData.IsBigWin) {
            return null;
        }

        return this.translation.translate('GAMING_HISTORY_BIG_WIN_TAX_INDICATION')
    }

    private get _isFreePlayRound(): boolean {
        return !Check.isNullOrUndefined(this.recordData.FreePlayCurrentAmount)
            || !Check.isNullOrUndefined(this.recordData.FreePlayWinningAmount);
    }

    private _getRealMoneyAmounts(): IHistoryRecordAmountViewModel[] {
        return [
            {
                description: this.translation.translate('GAMING_HISTORY_BET'),
                value: this._formatValue(this.recordData.BetAmount),
                cyClass: CyClass('cy-gaming-history-bet-value')
            },
            {
                description: Check.isNullOrUndefined(this.recordData.OriginalWinningsAmount)
                    ? this.translation.translate('GAMING_HISTORY_WINNINGS')
                    : this.translation.translate('GAMING_HISTORY_WINNINGS_BONUS_CAP'),
                value: this._formatValue(this.recordData.ProfitAmount),
                cyClass: CyClass('cy-gaming-history-winning-value')
            },
            {
                description: this.translation.translate('GAMING_HISTORY_BANKROLL'),
                value: this._formatValue(this.recordData.AvailableMoneyPerOffering),
                cyClass: CyClass('cy-gaming-history-bankroll-value')
            }
        ];
    }

    private _getFreePlayAmounts(): IHistoryRecordAmountViewModel[] {
        const amounts = [
            {
                description: this.translation.translate('GAMING_HISTORY_FREE_PLAY_BET'),
                value: this._formatValue(this.recordData.BetAmount),
                cyClass: CyClass('cy-gaming-history-bet-value')
            },
            {
                description: this.translation.translate('GAMING_HISTORY_FREE_PLAY_WINNINGS'),
                value: this._formatValue(this.recordData.ProfitAmount),
                cyClass: CyClass('cy-gaming-history-winning-value')
            },
            {
                description: this.translation.translate('GAMING_HISTORY_FREE_PLAY_REWARD_WINNINGS'),
                value: this._formatValue(this.recordData.FreePlayWinningAmount),
                cyClass: CyClass('cy-gaming-history-free-play-winning')
            }
        ];

        if(!Check.isNullOrUndefined(this.recordData.FreePlayCurrentAmount)) {
            amounts.push({
                description: this.translation.translate('GAMING_HISTORY_FREE_PLAY_REWARD_BANKROLL'),
                value: this._formatValue(this.recordData.FreePlayCurrentAmount),
                cyClass: CyClass('cy-gaming-history-free-play-current-amount')
            });
        }

        return amounts;
    }

    get amounts(): IHistoryRecordAmountViewModel[] {
        if(this._isFreePlayRound) {
            return this._getFreePlayAmounts();
        } else {
            return this._getRealMoneyAmounts();
        }
    }

    get freePlayMessage(): string | null {
        if(this._isFreePlayRound) {
            return this.translation.translate('GAMING_HISTORY_FREE_PLAY_INDICATION')
        }

        return null;
    }

    private get _gameSessionID(): string | null {
        return this.recordData.AdditionalData?.AAMSGameSessionID || null
    }

    private get _participationTicketID(): string | null {
        return this.recordData.AdditionalData?.AAMSParticipationTicketID || null
    }

    private _isDetailHistoryAllowedOnCurrentPlatform(): boolean {
        switch (this.recordData.AllowedPlatformType?.toLowerCase()) {
            case 'all':
                return true;
            case 'mobile':
                return this.services.device.os.isMobileOS;
            case 'pc':
                return !this.services.device.os.isMobileOS;
            default:
                return false;
        }
    }

    get detailsHistoryNotAllowedOnCurrentPlatformMessage(): string | null {
        if(this._isDetailHistoryAllowedOnCurrentPlatform()) {
            return null;
        }

        if(this.services.device.os.isMobileOS) {
            return this.translation.translate('GAMING_HISTORY_MORE_DETAILS_NOT_AVAILABLE_MOBILE');
        } else {
            return this.translation.translate('GAMING_HISTORY_MORE_DETAILS_NOT_AVAILABLE_PC');
        }
    }

    async openDetailHistory(): Promise<void> {
        if(!this.supportsDetailHistory) {
            return;
        }

        await this.services.gameRunner.openGame({
            gameMode: GameModeEnum.History,
            gameInfo: this.gameInfo,
            historyGameParams: {
                gameRoundId: parseInt(this.recordData.GameId),
                gameRoundDate: this.recordData.Date,
                gameRoundDateInTimeZone: this.recordData.DateInTimeZone
            }
        });
    }
}